/**
 * Oath Transfer Bond Service
 * API calls for transfer bond operations using the new Oath authentication
 */

import { OathStorageKeys } from '../models/userModels';
import { oathRefreshToken } from './oathAuthService';

const BASE_URL = process.env.REACT_APP_BASE_URL;

/**
 * Get auth headers with JWT token
 */
const getAuthHeaders = () => {
  const token = localStorage.getItem(OathStorageKeys.JWT_TOKEN);
  return {
    'Authorization': `Bearer ${token}`,
    'Content-Type': 'application/json',
  };
};

/**
 * Authenticated fetch wrapper with auto-refresh on 401
 */
const authFetch = async (url, options = {}) => {
  const response = await fetch(url, {
    ...options,
    headers: {
      ...getAuthHeaders(),
      ...options.headers,
    },
  });

  // If unauthorized, try to refresh token and retry
  if (response.status === 401) {
    const refreshResult = await oathRefreshToken();
    if (refreshResult.success) {
      // Retry with new token
      return fetch(url, {
        ...options,
        headers: {
          ...getAuthHeaders(),
          ...options.headers,
        },
      });
    }
  }

  return response;
};

/**
 * Parse API response
 */
const parseResponse = async (response) => {
  const data = await response.json().catch(() => ({}));
  return data.result !== undefined ? data.result : data;
};

// ============================================
// REQUESTING ENDPOINTS
// ============================================

/**
 * Create a new transfer bond request
 */
export const createTransferBondRequest = async (request) => {
  try {
    const response = await authFetch(`${BASE_URL}oath/transferbond/request`, {
      method: 'POST',
      body: JSON.stringify(request),
    });

    const result = await parseResponse(response);

    if (!response.ok) {
      return { success: false, error: result.message || 'Failed to create request' };
    }

    return { success: true, transferBondTransactionId: result.transferBondTransactionId || result };
  } catch (error) {
    console.error('Create transfer bond request error:', error);
    return { success: false, error: 'Network error' };
  }
};

/**
 * Edit an existing transfer bond request
 */
export const editTransferBondRequest = async (id, request) => {
  try {
    const response = await authFetch(`${BASE_URL}oath/transferbond/request/${id}`, {
      method: 'PUT',
      body: JSON.stringify(request),
    });

    const result = await parseResponse(response);

    if (!response.ok) {
      return { success: false, error: result.message || 'Failed to edit request' };
    }

    return { success: true, transferBondTransactionId: result.transferBondTransactionId };
  } catch (error) {
    console.error('Edit transfer bond request error:', error);
    return { success: false, error: 'Network error' };
  }
};

/**
 * Get all transfer bond requests for user's agencies
 */
export const getTransferBondRequests = async () => {
  try {
    const response = await authFetch(`${BASE_URL}oath/transferbond/requests`);
    const result = await parseResponse(response);

    if (!response.ok) {
      return { success: false, error: result.message || 'Failed to get requests', data: [] };
    }

    return { success: true, data: result };
  } catch (error) {
    console.error('Get transfer bond requests error:', error);
    return { success: false, error: 'Network error', data: [] };
  }
};

/**
 * Get transfer bond request details
 */
export const getTransferBondDetails = async (id) => {
  try {
    const response = await authFetch(`${BASE_URL}oath/transferbond/request/${id}`);
    const result = await parseResponse(response);

    if (!response.ok) {
      return { success: false, error: result.message || 'Failed to get details' };
    }

    return { success: true, data: result };
  } catch (error) {
    console.error('Get transfer bond details error:', error);
    return { success: false, error: 'Network error' };
  }
};

/**
 * Cancel a transfer bond request
 */
export const cancelTransferBondRequest = async (id, reason) => {
  try {
    const response = await authFetch(`${BASE_URL}oath/transferbond/request/${id}/cancel`, {
      method: 'PATCH',
      body: JSON.stringify({ reason }),
    });

    const result = await parseResponse(response);

    if (!response.ok) {
      return { success: false, error: result.message || 'Failed to cancel request' };
    }

    return { success: true };
  } catch (error) {
    console.error('Cancel transfer bond request error:', error);
    return { success: false, error: 'Network error' };
  }
};

/**
 * Select a quote for a transfer bond request
 */
export const selectQuote = async (id, quoteRequest) => {
  try {
    const response = await authFetch(`${BASE_URL}oath/transferbond/request/${id}/select-quote`, {
      method: 'PATCH',
      body: JSON.stringify(quoteRequest),
    });

    const result = await parseResponse(response);

    if (!response.ok) {
      return { success: false, error: result.message || 'Failed to select quote' };
    }

    return { success: true };
  } catch (error) {
    console.error('Select quote error:', error);
    return { success: false, error: 'Network error' };
  }
};

/**
 * Decline a quote for a transfer bond request
 */
export const declineQuote = async (id, declineRequest) => {
  try {
    const response = await authFetch(`${BASE_URL}oath/transferbond/request/${id}/decline-quote`, {
      method: 'PATCH',
      body: JSON.stringify(declineRequest),
    });

    const result = await parseResponse(response);

    if (!response.ok) {
      return { success: false, error: result.message || 'Failed to decline quote' };
    }

    return { success: true };
  } catch (error) {
    console.error('Decline quote error:', error);
    return { success: false, error: 'Network error' };
  }
};

/**
 * Get cancelled requests (requesting side)
 */
export const getCancelledRequests = async (retrieveAll = false) => {
  try {
    const url = `${BASE_URL}oath/transferbond/requests/cancelled${retrieveAll ? '?retrieveAll=true' : ''}`;
    const response = await authFetch(url);
    const result = await parseResponse(response);

    if (!response.ok) {
      return { success: false, error: result.message || 'Failed to get cancelled requests', data: [] };
    }

    return { success: true, data: result };
  } catch (error) {
    console.error('Get cancelled requests error:', error);
    return { success: false, error: 'Network error', data: [] };
  }
};

/**
 * Get closed requests (requesting side)
 * @param {string|Date} startDate - Start date (ISO string or Date object)
 * @param {string|Date} endDate - End date (ISO string or Date object)
 */
export const getClosedRequests = async (startDate, endDate) => {
  try {
    const params = new URLSearchParams();
    if (startDate) {
      const startStr = typeof startDate === 'string' ? startDate : startDate.toISOString();
      params.append('startDate', startStr);
    }
    if (endDate) {
      const endStr = typeof endDate === 'string' ? endDate : endDate.toISOString();
      params.append('endDate', endStr);
    }

    const url = `${BASE_URL}oath/transferbond/requests/closed${params.toString() ? '?' + params.toString() : ''}`;
    const response = await authFetch(url);
    const result = await parseResponse(response);

    if (!response.ok) {
      return { success: false, error: result.message || 'Failed to get closed requests', data: [] };
    }

    return { success: true, data: result };
  } catch (error) {
    console.error('Get closed requests error:', error);
    return { success: false, error: 'Network error', data: [] };
  }
};

/**
 * Get active agents by county (for selecting posting agency)
 */
export const getActiveAgentsByCounty = async (countyId) => {
  try {
    const response = await authFetch(`${BASE_URL}oath/transferbond/agents-by-county/${countyId}`);
    const result = await parseResponse(response);

    if (!response.ok) {
      return { success: false, error: result.message || 'Failed to get agents', data: [] };
    }

    return { success: true, data: result };
  } catch (error) {
    console.error('Get active agents by county error:', error);
    return { success: false, error: 'Network error', data: [] };
  }
};

/**
 * Get underwriting authority for an agency
 */
export const getUnderwritingAuthority = async (agencyId) => {
  try {
    const response = await authFetch(`${BASE_URL}oath/transferbond/${agencyId}/underwriting-authority`);
    const result = await parseResponse(response);

    if (!response.ok) {
      return { success: false, error: result.message || 'Failed to get underwriting authority' };
    }

    return { success: true, data: result };
  } catch (error) {
    console.error('Get underwriting authority error:', error);
    return { success: false, error: 'Network error' };
  }
};

// ============================================
// POSTING ENDPOINTS
// ============================================

/**
 * Get open requests for agency to quote
 */
export const getOpenRequestsForAgency = async (agencyId) => {
  try {
    const response = await authFetch(`${BASE_URL}oath/transferbond/${agencyId}/open-requests`);
    const result = await parseResponse(response);

    if (!response.ok) {
      return { success: false, error: result.message || 'Failed to get open requests', data: [] };
    }

    return { success: true, data: result };
  } catch (error) {
    console.error('Get open requests error:', error);
    return { success: false, error: 'Network error', data: [] };
  }
};

/**
 * Get quoted requests for agency
 */
export const getQuotedRequestsForAgency = async (agencyId) => {
  try {
    const response = await authFetch(`${BASE_URL}oath/transferbond/${agencyId}/quoted-requests`);
    const result = await parseResponse(response);

    if (!response.ok) {
      return { success: false, error: result.message || 'Failed to get quoted requests', data: [] };
    }

    return { success: true, data: result };
  } catch (error) {
    console.error('Get quoted requests error:', error);
    return { success: false, error: 'Network error', data: [] };
  }
};

/**
 * Get accepted requests for agency
 */
export const getAcceptedRequestsForAgency = async (agencyId) => {
  try {
    const response = await authFetch(`${BASE_URL}oath/transferbond/${agencyId}/accepted-requests`);
    const result = await parseResponse(response);

    if (!response.ok) {
      return { success: false, error: result.message || 'Failed to get accepted requests', data: [] };
    }

    return { success: true, data: result };
  } catch (error) {
    console.error('Get accepted requests error:', error);
    return { success: false, error: 'Network error', data: [] };
  }
};

/**
 * Get cancelled requests for agency (posting side)
 */
export const getCancelledRequestsForAgency = async (agencyId, retrieveAll = false) => {
  try {
    const url = `${BASE_URL}oath/transferbond/${agencyId}/cancelled${retrieveAll ? '?retrieveAll=true' : ''}`;
    const response = await authFetch(url);
    const result = await parseResponse(response);

    if (!response.ok) {
      return { success: false, error: result.message || 'Failed to get cancelled requests', data: [] };
    }

    return { success: true, data: result };
  } catch (error) {
    console.error('Get cancelled requests for agency error:', error);
    return { success: false, error: 'Network error', data: [] };
  }
};

/**
 * Get closed requests for agency (posting side)
 * @param {number} agencyId - Agency ID
 * @param {string|Date} startDate - Start date (ISO string or Date object)
 * @param {string|Date} endDate - End date (ISO string or Date object)
 */
export const getClosedRequestsForAgency = async (agencyId, startDate, endDate) => {
  try {
    const params = new URLSearchParams();
    if (startDate) {
      const startStr = typeof startDate === 'string' ? startDate : startDate.toISOString();
      params.append('startDate', startStr);
    }
    if (endDate) {
      const endStr = typeof endDate === 'string' ? endDate : endDate.toISOString();
      params.append('endDate', endStr);
    }

    const url = `${BASE_URL}oath/transferbond/${agencyId}/closed${params.toString() ? '?' + params.toString() : ''}`;
    const response = await authFetch(url);
    const result = await parseResponse(response);

    if (!response.ok) {
      return { success: false, error: result.message || 'Failed to get closed requests', data: [] };
    }

    return { success: true, data: result };
  } catch (error) {
    console.error('Get closed requests for agency error:', error);
    return { success: false, error: 'Network error', data: [] };
  }
};

/**
 * Submit a quote for a transfer bond request
 */
export const submitQuote = async (transactionId, quoteRequest) => {
  try {
    const response = await authFetch(`${BASE_URL}oath/transferbond/${transactionId}/quote`, {
      method: 'POST',
      body: JSON.stringify(quoteRequest),
    });

    const result = await parseResponse(response);

    if (!response.ok) {
      return { success: false, error: result.message || 'Failed to submit quote' };
    }

    return { success: true };
  } catch (error) {
    console.error('Submit quote error:', error);
    return { success: false, error: 'Network error' };
  }
};

/**
 * Get default quote values for agency
 */
export const getDefaultQuoteValues = async (agencyId, transactionId) => {
  try {
    const response = await authFetch(`${BASE_URL}oath/transferbond/${agencyId}/default-quotes/${transactionId}`);
    const result = await parseResponse(response);

    if (!response.ok) {
      return { success: false, error: result.message || 'Failed to get default quotes' };
    }

    return { success: true, data: result };
  } catch (error) {
    console.error('Get default quote values error:', error);
    return { success: false, error: 'Network error' };
  }
};

/**
 * Get unexecuted powers for agency
 */
export const getUnexecutedPowers = async (agencyId) => {
  try {
    const response = await authFetch(`${BASE_URL}oath/transferbond/${agencyId}/unexecuted-powers`);
    const result = await parseResponse(response);

    if (!response.ok) {
      return { success: false, error: result.message || 'Failed to get unexecuted powers', data: [] };
    }

    return { success: true, data: result };
  } catch (error) {
    console.error('Get unexecuted powers error:', error);
    return { success: false, error: 'Network error', data: [] };
  }
};

/**
 * Get transfer powers for a bond
 */
export const getTransferPowers = async (tbondId) => {
  try {
    const response = await authFetch(`${BASE_URL}oath/transferbond/${tbondId}/transfer-powers`);
    const result = await parseResponse(response);

    if (!response.ok) {
      return { success: false, error: result.message || 'Failed to get transfer powers', data: [] };
    }

    return { success: true, data: result };
  } catch (error) {
    console.error('Get transfer powers error:', error);
    return { success: false, error: 'Network error', data: [] };
  }
};

/**
 * Assign powers to a transaction
 */
export const createTransferPowers = async (transactionId, powersDto) => {
  try {
    const response = await authFetch(`${BASE_URL}oath/transferbond/${transactionId}/transfer-powers`, {
      method: 'POST',
      body: JSON.stringify(powersDto),
    });

    const result = await parseResponse(response);

    if (!response.ok) {
      return { success: false, error: result.message || 'Failed to assign powers' };
    }

    return { success: true };
  } catch (error) {
    console.error('Create transfer powers error:', error);
    return { success: false, error: 'Network error' };
  }
};

/**
 * Update power assignment
 */
export const updateTransferPowers = async (transactionId, tbondId, powersDto) => {
  try {
    const response = await authFetch(`${BASE_URL}oath/transferbond/${transactionId}/transfer-powers/${tbondId}`, {
      method: 'PUT',
      body: JSON.stringify(powersDto),
    });

    const result = await parseResponse(response);

    if (!response.ok) {
      return { success: false, error: result.message || 'Failed to update powers' };
    }

    return { success: true };
  } catch (error) {
    console.error('Update transfer powers error:', error);
    return { success: false, error: 'Network error' };
  }
};

/**
 * Remove power assignment
 */
export const deleteTransferPowers = async (transactionId, tbondId) => {
  try {
    const response = await authFetch(`${BASE_URL}oath/transferbond/${transactionId}/transfer-powers/${tbondId}`, {
      method: 'DELETE',
    });

    const result = await parseResponse(response);

    if (!response.ok) {
      return { success: false, error: result.message || 'Failed to remove powers' };
    }

    return { success: true };
  } catch (error) {
    console.error('Delete transfer powers error:', error);
    return { success: false, error: 'Network error' };
  }
};

/**
 * Mark transaction ready for verification
 */
export const markReadyForVerification = async (transactionId) => {
  try {
    const response = await authFetch(`${BASE_URL}oath/transferbond/${transactionId}/ready-for-verification`, {
      method: 'PATCH',
    });

    const result = await parseResponse(response);

    if (!response.ok) {
      return { success: false, error: result.message || 'Failed to mark ready' };
    }

    return { success: true };
  } catch (error) {
    console.error('Mark ready for verification error:', error);
    return { success: false, error: 'Network error' };
  }
};

/**
 * Clear declined request
 */
export const clearDeclinedRequest = async (transactionId, agencyId) => {
  try {
    const response = await authFetch(`${BASE_URL}oath/transferbond/${transactionId}/clear/${agencyId}`, {
      method: 'PATCH',
    });

    const result = await parseResponse(response);

    if (!response.ok) {
      return { success: false, error: result.message || 'Failed to clear request' };
    }

    return { success: true };
  } catch (error) {
    console.error('Clear declined request error:', error);
    return { success: false, error: 'Network error' };
  }
};

/**
 * Cancel pending request
 */
export const cancelPendingRequest = async (transactionId, agencyId) => {
  try {
    const response = await authFetch(`${BASE_URL}oath/transferbond/${transactionId}/cancel-pending/${agencyId}`, {
      method: 'PATCH',
    });

    const result = await parseResponse(response);

    if (!response.ok) {
      return { success: false, error: result.message || 'Failed to cancel pending request' };
    }

    return { success: true };
  } catch (error) {
    console.error('Cancel pending request error:', error);
    return { success: false, error: 'Network error' };
  }
};

/**
 * Upload documents for a transfer bond transaction
 */
export const uploadDocuments = async (transactionId, files) => {
  try {
    const token = localStorage.getItem(OathStorageKeys.JWT_TOKEN);
    const formData = new FormData();

    for (const file of files) {
      formData.append('files', file);
    }

    const response = await fetch(`${BASE_URL}oath/transferbond/${transactionId}/upload`, {
      method: 'POST',
      headers: {
        'Authorization': `Bearer ${token}`,
      },
      body: formData,
    });

    const result = await parseResponse(response);

    if (!response.ok) {
      return { success: false, error: result.message || 'Failed to upload documents' };
    }

    return { success: true, filesReceived: result.filesReceived };
  } catch (error) {
    console.error('Upload documents error:', error);
    return { success: false, error: 'Network error' };
  }
};

/**
 * Get uploaded file count for a transaction
 */
export const getFileCount = async (transactionId) => {
  try {
    const response = await authFetch(`${BASE_URL}oath/transferbond/${transactionId}/file-count`);
    const result = await parseResponse(response);

    if (!response.ok) {
      return { success: false, error: result.message || 'Failed to get file count' };
    }

    return { success: true, count: result.count, powerCount: result.powerCount };
  } catch (error) {
    console.error('Get file count error:', error);
    return { success: false, error: 'Network error' };
  }
};

/**
 * Get document pages for a transaction
 */
export const getDocumentPages = async (transactionId) => {
  try {
    const response = await authFetch(`${BASE_URL}oath/transferbond/${transactionId}/pages`);
    const result = await parseResponse(response);

    if (!response.ok) {
      return { success: false, error: result.message || 'Failed to get document pages' };
    }

    const pages = Array.isArray(result) ? result : (result.pages || []);
    return { success: true, data: pages };
  } catch (error) {
    console.error('Get document pages error:', error);
    return { success: false, error: 'Network error' };
  }
};

/**
 * Download a document page file by pageId.
 * Returns a blob with the file content.
 */
export const downloadDocumentPage = async (transactionId, pageId) => {
  try {
    const response = await authFetch(`${BASE_URL}oath/transferbond/${transactionId}/page/${pageId}`);

    if (!response.ok) {
      return { success: false, error: `Failed to download file: ${response.statusText}` };
    }

    const blob = await response.blob();
    const contentType = response.headers.get('Content-Type') || '';
    const disposition = response.headers.get('Content-Disposition') || '';
    const fileNameMatch = disposition.match(/filename[^;=\n]*=(['"]?)(.+?)\1(;|$)/);
    const fileName = fileNameMatch ? fileNameMatch[2] : `document-${pageId}`;

    return { success: true, blob, contentType, fileName };
  } catch (error) {
    console.error('Download document page error:', error);
    return { success: false, error: 'Network error' };
  }
};

// ============================================
// LOCATION ENDPOINTS
// ============================================

/**
 * Get all states
 */
export const getStates = async () => {
  try {
    const response = await authFetch(`${BASE_URL}oath/location/states`);
    const result = await parseResponse(response);

    if (!response.ok) {
      return { success: false, error: result.message || 'Failed to get states', data: [] };
    }

    return { success: true, data: result };
  } catch (error) {
    console.error('Get states error:', error);
    return { success: false, error: 'Network error', data: [] };
  }
};

/**
 * Get counties by state
 */
export const getCountiesByState = async (stateCd) => {
  try {
    const response = await authFetch(`${BASE_URL}oath/location/countiesByState/${stateCd}`);
    const result = await parseResponse(response);

    if (!response.ok) {
      return { success: false, error: result.message || 'Failed to get counties', data: [] };
    }

    return { success: true, data: result };
  } catch (error) {
    console.error('Get counties error:', error);
    return { success: false, error: 'Network error', data: [] };
  }
};

/**
 * Get counties/territories for an agency (default quote settings per county)
 */
export const getCountiesByAgency = async (agencyId) => {
  try {
    const response = await authFetch(`${BASE_URL}oath/location/countiesByAgencies/${agencyId}`);
    const result = await parseResponse(response);

    if (!response.ok) {
      return { success: false, error: result.message || 'Failed to get agency territories', data: [] };
    }

    return { success: true, data: result };
  } catch (error) {
    console.error('Get agency territories error:', error);
    return { success: false, error: 'Network error', data: [] };
  }
};

/**
 * Update county default quote settings for an agency territory
 */
export const updateCountyDefaults = async (agentCountyID, countyData) => {
  try {
    const response = await authFetch(`${BASE_URL}oath/location/countiesByAgencies/${agentCountyID}`, {
      method: 'PATCH',
      body: JSON.stringify(countyData),
    });

    if (!response.ok) {
      const result = await parseResponse(response);
      return { success: false, error: result.message || 'Failed to update county defaults' };
    }

    return { success: true };
  } catch (error) {
    console.error('Update county defaults error:', error);
    return { success: false, error: 'Network error' };
  }
};
